import { FiSave } from 'react-icons/fi';
import styles from './OpeningBalance.module.css';
import { formatMoney } from './openingBalanceUtils';

/** Prevent mouse-wheel scroll from changing a focused number input's value */
const blockWheel = (e) => e.target.blur();

/** Block decimal point / exponent / sign keys so only whole numbers can be typed */
const blockNonInteger = (e) => {
  if (['.', ',', 'e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
};

/** Coerce any input to a whole-number string (truncates decimals, e.g. pasted "501.44" → "501") */
const toIntString = (v) => String(v ?? '').split('.')[0].replace(/[^\d]/g, '');

export default function LedgerBalancesTab({
  accounts,
  assetLines,
  readOnly,
  frozen,
  saving,
  onSave,
  onFieldChange,
  isLockedAccount,
}) {
  return (
    <div className="card">
      <div className="card-header">
        <h3>Ledger Opening Balances</h3>
        {!frozen && !readOnly && (
          <button className="btn btn-primary btn-sm" onClick={onSave} disabled={saving}>
            <FiSave /> {saving ? 'Saving...' : 'Save Assets'}
          </button>
        )}
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Account</th>
              <th>Group</th>
              <th>Sub-group</th>
              <th>Nature</th>
              <th>Dr / Cr</th>
              <th>Debit / Credit</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((account) => {
              const line = assetLines[account.id] || {};
              const locked = isLockedAccount?.(account);
              const lockMessage = account.subGroup?.name === 'Donation Income'
                  ? 'Auto-updated from Category Fund Balances.'
                  : locked
                    ? 'Locked for opening balance.'
                    : null;

              return (
                <tr key={account.id} className={styles.obRow}>
                  <td>
                    <strong>{account.name}</strong>
                    <br />
                    <small style={{ color: 'var(--color-text-muted)' }}>{account.accountCode || 'No code'}</small>
                    {account.description && (
                      <>
                        <br />
                        <small style={{ color: 'var(--color-text-muted)' }}>{account.description}</small>
                      </>
                    )}
                    {lockMessage && (
                      <>
                        <br />
                        <small style={{ color: 'var(--color-text-muted)' }}>{lockMessage}</small>
                      </>
                    )}
                  </td>
                  <td>{account.group?.name || '-'}</td>
                  <td>{account.subGroup?.name || '-'}</td>
                  <td><span className="badge badge-pending">{account.group?.nature || account.accountType || '-'}</span></td>
                  <td>
                    {readOnly || frozen || locked ? (
                      <strong>
                        {(line.nature === 'credit' ? 'Cr' : line.nature === 'debit' ? 'Dr' : account.group?.normalBalance) || '-'}
                      </strong>
                    ) : (
                      <select
                        value={line.nature || (account.group?.normalBalance === 'Cr' ? 'credit' : 'debit')}
                        onChange={(e) => onFieldChange(account.id, 'nature', e.target.value)}
                        style={{ padding: '4px 6px', fontSize: '0.85rem', borderRadius: 6, border: '1.5px solid var(--color-border)' }}
                      >
                        <option value="debit">Dr</option>
                        <option value="credit">Cr</option>
                      </select>
                    )}
                  </td>
                  <td>
                    {readOnly ? (
                      <span className={styles.amtRight}>Rs {formatMoney(line.amountInr || 0)}</span>
                    ) : (
                      <input
                        className={styles.obInput}
                        type="number"
                        min="0"
                        step="1"
                        value={line.amountInr ?? ''}
                        onWheel={blockWheel}
                        onKeyDown={blockNonInteger}
                        onChange={(event) => onFieldChange(account.id, 'amountInr', toIntString(event.target.value))}
                        disabled={frozen || locked}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
